// src/components/Shared/OfflineBanner.jsx
// Persistent banner reflecting the connectivity mode from NetworkContext.
//   - online         → no banner
//   - cloud-degraded → amber "Syncing N pending action(s)" banner
//   - queueing       → red "You are offline" banner with queue count
//   - broker         → green "Connected to venue broker" banner (Phase 2)
// See docs/system/offline-mode.md.

import React from 'react';
import { useNetwork } from '../../context/NetworkContext';
import { IconSync, IconWifiOff, IconWifi } from './FigmaIcons';

const MODES = {
	'cloud-degraded': {
		tone: 'warning',
		Icon: IconSync,
		text: (queue) => `Syncing ${queue} pending action${queue === 1 ? '' : 's'}…`,
	},
	queueing: {
		tone: 'danger',
		Icon: IconWifiOff,
		text: (queue) =>
			queue > 0
				? `You're offline. ${queue} action${queue === 1 ? '' : 's'} will sync when the connection returns.`
				: "You're offline. New actions will queue and sync automatically.",
	},
	broker: {
		tone: 'success',
		Icon: IconWifi,
		text: () => 'Connected to venue broker. Orders flowing via the local network.',
	},
};

const OfflineBanner = () => {
	const { connectivityMode = 'online', queueLength = 0, isOnline } = useNetwork() || {};

	const mode =
		connectivityMode === 'online'
			? null
			: connectivityMode ||
			  (!isOnline ? 'queueing' : queueLength > 0 ? 'cloud-degraded' : null);

	if (!mode) return null;
	const config = MODES[mode];
	if (!config) return null;
	const { Icon } = config;

	return (
		<div
			className={`ev-offline-banner ev-offline-tone-${config.tone}`}
			role="alert"
			data-connectivity-mode={mode}
		>
			<Icon className="ev-offline-banner-icon" />
			<span className="ev-offline-banner-text">{config.text(queueLength)}</span>
			{queueLength > 0 && (
				<span className="ev-offline-banner-count">{queueLength}</span>
			)}
		</div>
	);
};

export default OfflineBanner;
